<template>
  <div class="md:max-w-screen-xl">
    <div class="rounded-lg bg-white px-5 py-5 shadow-sm hidden md:block">
      <h5 class="text-brand-black text-2xl">Applicants</h5>

      <div class="flex flex-row pt-5 pb-3">
        <router-link
          :to="{
            name: 'dashboard_applicants_submitted'
          }"
        >
          <StyledTab :active="$route.name === 'dashboard_applicants_submitted'">
            Submitted
          </StyledTab>
        </router-link>

        <router-link
          :to="{
            name: 'dashboard_applicants_invited'
          }"
        >
          <StyledTab :active="$route.name === 'dashboard_applicants_invited'"> Invited </StyledTab>
        </router-link>

        <router-link
          :to="{
            name: 'dashboard_applicants_archived'
          }"
        >
          <StyledTab :active="$route.name === 'dashboard_applicants_archived'">
            Archived
          </StyledTab>
        </router-link>

        <router-link
          :to="{
            name: 'dashboard_applicants_decided'
          }"
        >
          <StyledTab :active="$route.name === 'dashboard_applicants_decided'"> Decided </StyledTab>
        </router-link>
      </div>

      <div class="flex flex-row pb-5 relative">
        <!-- START SEARCH RESULTS -->
        <div class="w-1/2">
          <SearchInput @search="handleSearch" />
        </div>
        <!-- END SEARCH INPUT DEFAULT -->

        <template v-if="status === 'submitted'">
          <button
            class="flex flex-row border-2 rounded-lg py-1.5 px-2 items-center"
            :class="[
              filterApplied
                ? 'bg-brand-light-gray border-brand-green'
                : 'border-brand-background-light-blue hover:bg-brand-background-blue-gray'
            ]"
            @click="openFilterMenu"
          >
            <div class="flex items-center mr-1">
              <FilterIcon class="w-4 h-5 text-brand-green" />
            </div>
            <div class="flex items-center">
              <p class="text-brand-green">
                {{ filterCount > 0 ? `${filterCount} filters applied` : "Apply filter" }}
              </p>
            </div>
          </button>
        </template>
        <template v-if="status === 'decided'">
          <div class="rounded-lg flex flex-row bg-brand-light-gray py-1 px-1 ml-5">
            <div
              class="flex items-center rounded py-1 px-2 mx-1 cursor-pointer"
              :class="[currentDecidedState === 'decided' ? 'bg-brand-background-light-blue' : '']"
              @click="setDecidedState('decided')"
            >
              <p
                :class="[
                  'text-sm',
                  currentDecidedState === 'decided' ? 'text-brand-blue' : 'text-brand-gray'
                ]"
              >
                Both states
              </p>
            </div>

            <div
              class="flex items-center rounded py-1 px-2 mx-1 cursor-pointer"
              :class="[currentDecidedState === 'accepted' ? 'bg-brand-background-light-blue' : '']"
              @click="setDecidedState('accepted')"
            >
              <p
                :class="[
                  'text-sm',
                  currentDecidedState === 'accepted' ? 'text-brand-blue' : 'text-brand-gray'
                ]"
              >
                Only accepted
              </p>
            </div>

            <div
              class="flex items-center rounded py-1 px-2 mx-1 cursor-pointer"
              :class="[currentDecidedState === 'declined' ? 'bg-brand-background-light-blue' : '']"
              @click="setDecidedState('declined')"
            >
              <p
                :class="[
                  'text-sm',
                  currentDecidedState === 'declined' ? 'text-brand-blue' : 'text-brand-gray'
                ]"
              >
                Only declined
              </p>
            </div>
          </div>
        </template>
      </div>

      <Line :bold="true" />

      <!-- START DESKTOP ROW HEADER-->
      <div class="flex flex-row w-full py-3">
        <div :class="['flex items-center', showActionBar ? 'pr-5' : 'w-1/12 py-3']">
          <input
            type="checkbox"
            class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
            v-model="selectAll"
          />
        </div>

        <template v-if="showActionBar">
          <div class="w-9/10 flex flex-row">
            <div class="flex flex-row items-center">
              <template
                v-if="selected.length === 1 && selected[0].applications[0].status === 'submitted'"
              >
                <div
                  class="mx-1"
                  v-if="
                    getCurrentUser.landlord && getCurrentUser.landlord.show_accept_reject_button
                  "
                >
                  <SecondaryButton :classes="'border-brand-red'" @click="openRejectModal">
                    Decline
                  </SecondaryButton>
                </div>
                <div
                  class="mx-1"
                  v-if="
                    getCurrentUser.landlord && getCurrentUser.landlord.show_accept_reject_button
                  "
                >
                  <PrimaryButton @click="openApproveModal" :classes="'whitespace-nowrap'">
                    Select as tenant
                  </PrimaryButton>
                </div>
              </template>
            </div>

            <div class="flex border border-brand-mid-gray mx-5"></div>

            <div class="flex flex-row items-center">
              <div class="w-32 mr-3" v-if="status !== 'archived'">
                <SecondaryButton @click="archive"> Archive </SecondaryButton>
              </div>
              <div class="w-32 mr-3" v-if="status === 'archived'">
                <SecondaryButton @click="unarchive"> Unarchive </SecondaryButton>
              </div>
              <div
                class="mr-3"
                v-if="status !== 'invited' && getCurrentUser && getCurrentUser.can_admin_company"
              >
                <SecondaryButton @click="openChangeOwnerModal" :classes="'border-brand-blue'">
                  Change Owner
                </SecondaryButton>
              </div>
              <div class="mr-3" v-if="selected.length > 1">
                <SecondaryButton @click="linkApplicants"> Link Applicants </SecondaryButton>
              </div>
              <div class="mr-3" v-if="selected.length === 1 && selected[0].applications.length > 1">
                <SecondaryButton @click="unlinkApplicants"> Unlink Applicants </SecondaryButton>
              </div>

              <div class="mr-3">
                <PrimaryButton
                  :classes="'bg-brand-red border-brand-red hover:bg-brand-red'"
                  @click="openDeleteApplicantModal"
                >
                  Delete Applicants
                </PrimaryButton>
              </div>
            </div>
          </div>
        </template>

        <template v-if="!showActionBar">
          <template
            v-if="
              ['dashboard_applicants_submitted', 'dashboard_applicants_archived'].includes(
                $route.name
              )
            "
          >
            <div class="w-1/4 flex items-center">
              <h5 class="text-lg font-normal">Applicant's name</h5>
            </div>

            <div class="w-1/4 flex items-center">
              <h5 class="text-lg font-normal">Applying to</h5>
            </div>

            <div class="w-5/12 px-1 flex items-center">
              <h5 class="text-lg px-1">Report status</h5>
            </div>

            <div class="w-1/4 px-1 flex items-center">
              <h5 class="text-lg">Share applicant</h5>
            </div>

            <div class="w-1/5 px-1 flex items-center">
              <h5 class="text-lg">App. history</h5>
            </div>
            <div class="w-10"></div>
          </template>
          <template v-else-if="$route.name === 'dashboard_applicants_invited'">
            <div class="w-1/3 flex items-center">
              <h5 class="text-lg font-normal">Applicant email</h5>
            </div>
            <div class="w-1/5 flex items-center">
              <h5 class="text-lg font-normal">Date sent</h5>
            </div>
            <div class="w-1/5 flex items-center">
              <h5 class="text-lg font-normal">Status</h5>
            </div>
            <div class="w-1/4"></div>
          </template>
          <template v-else>
            <div class="w-1/4 flex items-center">
              <h5 class="text-lg font-normal">Applicant's name</h5>
            </div>

            <div class="w-1/5 flex items-center">
              <h5 class="text-lg font-normal">Applying to</h5>
            </div>

            <div class="w-1/5 px-1 flex items-center">
              <h5 class="text-lg px-1">Date</h5>
            </div>

            <div class="w-1/5 px-1 flex items-center">
              <h5 class="text-lg">Decision</h5>
            </div>

            <div class="w-1/4 px-1 flex items-center">
              <h5 class="text-lg">Share</h5>
            </div>
            <div class="w-1/5 px-1 flex items-center">
              <h5 class="text-lg">App. history</h5>
            </div>
            <div class="w-10"></div>
          </template>
        </template>
      </div>
      <!-- END DESKTOP ROW HEADER-->
    </div>

    <div class="pt-3">
      <!-- START MOBILE SUB OPTIONS -->
      <div class="flex flex-row md:hidden px-2">
        <template v-if="status === 'submitted'">
          <button
            class="flex flex-row border-2 rounded-lg py-1.5 px-2 mx-1"
            :class="[
              filterApplied
                ? 'bg-brand-background-light-green border-brand-green'
                : 'border-brand-background-light-blue bg-white hover:bg-brand-background-blue-gray'
            ]"
            @click="openFilterMenu"
          >
            <div class="flex items-center mr-1">
              <FilterIcon class="w-4 h-5 text-brand-green" />
            </div>
            <div class="flex items-center">
              <p class="text-sm text-brand-green">
                {{ filterCount > 0 ? `${filterCount} filters applied` : "Apply filter" }}
              </p>
            </div>
          </button>
        </template>
        <template v-if="status === 'decided'">
          <div class="flex w-1/3 items-center py-2">
            <DropDown
              :showSelectedAsGray="false"
              :options="decidedStateOptions"
              :initial="decidedStateOptions.find((o) => o.value === 'decided')"
              @select="setDecidedState($event.value)"
            />
          </div>
        </template>
      </div>
      <!-- END MOBILE SUB OPTIONS -->

      <router-view :select-all="selectAll" @select="handleSelect" :key="$route.fullPath" />
    </div>
  </div>
  <template v-if="selected && selected.length > 0">
    <ApproveModal
      :showModal="showApproveModal"
      @close="closeApproveModal"
      :app="selected[0].applications[0]"
    />
    <RejectModal :showModal="showRejectModal" @close="closeRejectModal" :bundle="selected[0]" />
    <ChangeOwnerModal
      :showModal="showChangeOwnerModal"
      @close="closeChangeOwnerModal"
      :bundles="selected"
    />

    <DeleteApplicantsModal
      :showModal="showDeleteApplicantModal"
      @close="closeDeleteApplicantModal"
      :bundles="selected"
    />
  </template>
  <ActionToast ref="applicantsToast" />
  <FilterMenu :showMenu="showFilterMenu" @close="closeFilterMenu" :key="$route.fullPath" />
</template>
<script>
import { mapGetters } from "vuex"

import { FilterIcon } from "@heroicons/vue/outline"

import ChangeOwnerModal from "./components/ChangeOwnerModal.vue"
import FilterMenu from "./components/FilterMenu.vue"

import RejectModal from "@/views/dashboard/view_application/components/RejectModal.vue"
import ApproveModal from "@/views/dashboard/view_application/components/ApproveModal.vue"

import DeleteApplicantsModal from "./components/DeleteApplicantsModal.vue"

import {
  APPLICATION_BUNDLE_GET_LIST,
  APPLICATION_BUNDLE_ARCHIVE,
  APPLICATION_BUNDLE_UNARCHIVE,
  APPLICATION_BUNDLE_LINK,
  APPLICATION_BUNDLE_UNLINK
} from "@/store/types"

export default {
  components: {
    FilterIcon,
    RejectModal,
    ApproveModal,
    ChangeOwnerModal,
    FilterMenu,
    DeleteApplicantsModal
  },
  computed: {
    ...mapGetters([
      "getApplicationBundles",
      "getCurrentUser",
      "getApplicationBundleFilters",
      "getApplicationBundleSearchTerm",
      "getApplicationBundleStatus"
    ]),
    showActionBar() {
      return this.selected.length > 0 && this.status !== "invited"
    },
    filterApplied() {
      if (
        !this.getApplicationBundleFilters ||
        !this.getApplicationBundleFilters.reports ||
        !this.getApplicationBundleFilters.reports.length
      ) {
        return false
      }
      return !!this.getApplicationBundleFilters.reports
    },
    filterCount() {
      if (!this.filterApplied) {
        return 0
      }
      return this.getApplicationBundleFilters.reports.length
    }
  },
  mounted() {
    if (this.$route.name === "dashboard_applicants") {
      this.$router.push({ name: "dashboard_applicants_submitted" })
      return
    }

    this.status = this.$route.name.split("dashboard_applicants_")[1]
  },
  watch: {
    $route: {
      handler(val) {
        this.selected = []
        this.selectAll = false
        this.status = val.name.split("dashboard_applicants_")[1]
      },
      deep: true
    }
  },
  data() {
    return {
      selectAll: false,
      selected: [],
      status: "",
      showApproveModal: false,
      showRejectModal: false,
      showChangeOwnerModal: false,
      showDeleteApplicantModal: false,
      showFilterMenu: false,
      currentDecidedState: "decided",
      decidedStateOptions: [
        { value: "decided", label: "Both states" },
        { value: "accepted", label: "Only accepted " },
        { value: "declined", label: "Only declined " }
      ]
    }
  },
  methods: {
    async setDecidedState(state) {
      if (this.currentDecidedState !== state) {
        this.currentDecidedState = state
        try {
          await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
            status: state,
            search: this.getApplicationBundleSearchTerm
          })
        } catch (err) {
          console.log(err)
        }
      }
    },
    openFilterMenu() {
      this.showFilterMenu = true
    },
    async closeFilterMenu(data) {
      this.showFilterMenu = false

      if (data && (data.filters.reports || data.searchTerm)) {
        try {
          await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
            status: this.status,
            filters: data.filters,
            search: data.searchTerm
          })
        } catch (err) {
          console.log(err)
        }
      }
    },
    async handleSearch(val) {
      try {
        await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
          status: this.status,
          search: val
        })
      } catch (err) {
        console.log(err)
      }
    },
    handleSelect(selected) {
      this.selected = selected
      if (selected.length === 0) {
        this.selectedAll = false
      }
    },
    async reloadBundles() {
      await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
        status: this.status
      })
      this.selected = []
    },
    async archive() {
      const slugs = this.selected.map((bundle) => bundle.slug)
      await this.$store.dispatch(APPLICATION_BUNDLE_ARCHIVE, { slugs })
      this.$refs.applicantsToast.show({
        type: "success",
        hasClose: true,
        content: "Applicants archived",
        icon: "check"
      })
      await this.reloadBundles()
    },
    async unarchive() {
      const slugs = this.selected.map((bundle) => bundle.slug)
      await this.$store.dispatch(APPLICATION_BUNDLE_UNARCHIVE, { slugs })
      this.$refs.applicantsToast.show({
        type: "success",
        hasClose: true,
        content: "Applicants unarchived",
        icon: "check"
      })
      await this.reloadBundles()
    },
    async linkApplicants() {
      const ids = this.selected
        .map((bundle) => {
          return bundle.applications.map((app) => app.id)
        })
        .flat()

      await this.$store.dispatch(APPLICATION_BUNDLE_LINK, { ids })
      this.$refs.applicantsToast.show({
        type: "info",
        hasClose: true,
        content: "Applicants linked",
        icon: "check"
      })

      await this.reloadBundles()
    },
    async unlinkApplicants() {
      const slug = this.selected.map((bundle) => bundle.slug)[0]

      await this.$store.dispatch(APPLICATION_BUNDLE_UNLINK, { slug })
      this.$refs.applicantsToast.show({
        type: "info",
        hasClose: true,
        content: "Applicants unlinked",
        icon: "check"
      })

      await this.reloadBundles()
    },
    openDeleteApplicantModal() {
      this.showDeleteApplicantModal = true
    },
    closeDeleteApplicantModal(reload) {
      this.showDeleteApplicantModal = false

      if (reload) {
        this.$refs.applicantsToast.show({
          type: "success",
          hasClose: true,
          content: `Applicant${this.selected.length > 1 ? "s" : ""} deleted`,
          icon: "check"
        })

        this.reloadBundles()
      }
    },
    openApproveModal() {
      this.showApproveModal = true
    },
    closeApproveModal(reload) {
      this.showApproveModal = false
      if (reload) {
        this.$refs.applicantsToast.show({
          type: "success",
          hasClose: true,
          content: "Applicant approved",
          icon: "check"
        })

        this.reloadBundles()
      }
    },
    openRejectModal() {
      this.showRejectModal = true
    },
    closeRejectModal(reload) {
      this.showRejectModal = false
      if (reload) {
        this.$refs.applicantsToast.show({
          type: "danger",
          hasClose: true,
          content: "Applicant declined",
          icon: "check"
        })
        this.reloadBundles()
      }
    },
    openChangeOwnerModal() {
      this.showChangeOwnerModal = true
    },
    closeChangeOwnerModal(reload) {
      this.showChangeOwnerModal = false

      if (reload) {
        this.$refs.applicantsToast.show({
          type: "info",
          hasClose: true,
          content: "Application owner has been changed",
          icon: "check"
        })

        this.reloadBundles()
      }
    }
  }
}
</script>
